import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Plus, Trash2, X, Play, Pause, Archive, Save } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { TimingPicker } from '@/components/drip/timing-picker';
import {
  useDripCampaign,
  usePatchCampaign,
  useAddStep,
  usePatchStep,
  useDeleteStep,
  useRecallAudience,
  type DripStep,
  type Anchor,
  type Channel,
  type CampaignStatus,
} from '@/lib/drip';
import { estimateSegments } from '@/lib/sms';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/sms/campaigns/$id')({
  staticData: { crumb: 'Kampania' },
  component: CampaignDetailPage,
});

const ANCHOR_LABEL: Record<Anchor, string> = {
  booking_start: 'Początek wizyty',
  booking_end: 'Koniec wizyty',
  campaign_start: 'Start kampanii',
  prev_step: 'Poprzedni krok',
  last_visit: 'Ostatnia wizyta pacjenta',
};

/**
 * Which anchors are sensible per campaign kind. The full set is offered for
 * 'manual'/operator scenarios but we hide irrelevant choices in normal UX.
 */
const ANCHORS_BY_KIND: Record<string, Anchor[]> = {
  booking: ['booking_start', 'booking_end', 'prev_step'],
  recall: ['last_visit'],
  newsletter: ['campaign_start'],
};

function anchorsFor(kind: string): Anchor[] {
  return ANCHORS_BY_KIND[kind] ?? (['booking_start', 'last_visit', 'campaign_start'] as Anchor[]);
}

function CampaignDetailPage() {
  const { id } = Route.useParams();
  const { data, isPending } = useDripCampaign(id);
  const patch = usePatchCampaign(id);
  const [addingStep, setAddingStep] = React.useState(false);
  const [editingStepId, setEditingStepId] = React.useState<string | null>(null);

  if (isPending) {
    return (
      <div className="mx-auto max-w-4xl space-y-4">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (!data) {
    return <p className="text-sm text-error">Nie znaleziono kampanii.</p>;
  }
  const { campaign, steps } = data;

  const setStatus = (status: CampaignStatus) => patch.mutate({ status });

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to={'/sms/campaigns' as never}
            className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" /> Wszystkie kampanie
          </Link>
          <h1 className="mt-2 font-display text-2xl font-semibold tracking-tight text-text">
            {campaign.name}
          </h1>
          <p className="mt-1 text-sm text-text-muted">
            {campaign.kind} · {campaign.audience} · {steps.length}{' '}
            {steps.length === 1 ? 'krok' : 'kroków'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {campaign.status === 'active' ? (
            <Button variant="secondary" onClick={() => setStatus('paused')} disabled={patch.isPending}>
              <Pause className="size-4" aria-hidden="true" /> Wstrzymaj
            </Button>
          ) : campaign.status === 'paused' || campaign.status === 'draft' ? (
            <Button onClick={() => setStatus('active')} disabled={patch.isPending || steps.length === 0}>
              <Play className="size-4" aria-hidden="true" /> {campaign.status === 'draft' ? 'Aktywuj' : 'Wznów'}
            </Button>
          ) : null}
          {campaign.status !== 'archived' ? (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Zarchiwizuj"
              onClick={() => setStatus('archived')}
            >
              <Archive className="size-4" aria-hidden="true" />
            </Button>
          ) : null}
        </div>
      </header>

      {campaign.status === 'draft' && steps.length === 0 ? (
        <p className="rounded-md bg-warning/10 p-3 text-sm text-warning ring-1 ring-warning/20">
          Dodaj przynajmniej jeden krok, aby aktywować kampanię.
        </p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Kroki sekwencji</CardTitle>
          <CardDescription>
            Każdy krok wysyła SMS w momencie wyliczonym z punktu odniesienia. Cron uruchamia się
            co godzinę i wysyła tylko te kroki, które wpadają w okno ±1h.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {steps.length === 0 ? (
            <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
              Brak kroków. Dodaj pierwszy poniżej.
            </p>
          ) : (
            <ul role="list" className="space-y-3">
              {steps.map((step, i) => (
                <StepRow
                  key={step.id}
                  step={step}
                  index={i}
                  campaignId={id}
                  campaignKind={campaign.kind}
                  editing={editingStepId === step.id}
                  onStartEdit={() => setEditingStepId(step.id)}
                  onCloseEdit={() => setEditingStepId(null)}
                />
              ))}
            </ul>
          )}

          {addingStep ? (
            <AddStepForm
              campaignId={id}
              kind={campaign.kind}
              defaultAnchor={
                campaign.kind === 'recall'
                  ? 'last_visit'
                  : campaign.kind === 'newsletter'
                  ? 'campaign_start'
                  : 'booking_start'
              }
              defaultPosition={steps.length}
              onAdded={() => setAddingStep(false)}
              onCancel={() => setAddingStep(false)}
            />
          ) : (
            <Button type="button" variant="secondary" onClick={() => setAddingStep(true)}>
              <Plus className="size-4" aria-hidden="true" /> Dodaj krok
            </Button>
          )}
        </CardContent>
      </Card>

      {campaign.kind === 'recall' ? <RecallAudienceCard steps={steps} /> : null}
    </div>
  );
}

function RecallAudienceCard({ steps }: { steps: DripStep[] }) {
  // The recall window is encoded in the step offset (negative minutes from
  // last_visit). Use the first enabled last_visit step's offset to infer
  // the months window; fall back to 6 months.
  const recallStep = steps.find((s) => s.enabled && s.anchor === 'last_visit');
  const monthsAgo = recallStep
    ? Math.max(1, Math.min(36, Math.round(Math.abs(recallStep.offset_minutes) / (60 * 24 * 30))))
    : 6;
  const audience = useRecallAudience(monthsAgo, true);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Audiencja recall</CardTitle>
        <CardDescription>
          Liczba pacjentów, którzy mieli wizytę {monthsAgo} miesięcy temu lub
          wcześniej i nie mają jeszcze umówionej kolejnej. To są kandydaci na
          przypomnienie — gdy umówią się ponownie, automatycznie wypadają z kolejki.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {audience.isPending ? (
          <p className="text-sm text-text-muted">Liczę…</p>
        ) : audience.data ? (
          <div className="grid gap-3 md:grid-cols-3">
            <Metric label="Do wysłania teraz" value={audience.data.due_now} highlight />
            <Metric label="Do wysłania w tym tygodniu" value={audience.data.due_this_week} />
            <Metric label="Wszyscy pacjenci w bazie" value={audience.data.total_patients} muted />
          </div>
        ) : (
          <p className="text-sm text-error">Nie udało się policzyć audiencji.</p>
        )}
      </CardContent>
    </Card>
  );
}

function Metric({
  label,
  value,
  highlight,
  muted,
}: {
  label: string;
  value: number;
  highlight?: boolean;
  muted?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-md p-4 ring-1',
        highlight
          ? 'bg-accent/10 text-accent ring-accent/20'
          : muted
          ? 'bg-surface text-text-muted ring-border'
          : 'bg-surface text-text ring-black/5'
      )}
    >
      <p className="font-display text-3xl font-semibold tabular-nums">{value}</p>
      <p className="mt-1 text-xs uppercase tracking-wide">{label}</p>
    </div>
  );
}

function StepRow({
  step,
  index,
  campaignId,
  campaignKind,
  editing,
  onStartEdit,
  onCloseEdit,
}: {
  step: DripStep;
  index: number;
  campaignId: string;
  campaignKind: string;
  editing: boolean;
  onStartEdit: () => void;
  onCloseEdit: () => void;
}) {
  const patchStep = usePatchStep(campaignId);
  const deleteStep = useDeleteStep(campaignId);
  return (
    <li className="rounded-[var(--radius-card)] bg-bg p-4 ring-1 ring-black/5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-baseline gap-2">
            <span className="grid size-6 place-items-center rounded-full bg-accent/10 text-xs font-semibold text-accent ring-1 ring-accent/20">
              {index + 1}
            </span>
            <span className="font-medium text-text">{formatOffset(step)}</span>
            <span className="text-xs text-text-subtle">
              · {step.channel.toUpperCase()} · {ANCHOR_LABEL[step.anchor]}
            </span>
            {!step.enabled ? <Badge variant="outline">wyłączony</Badge> : null}
            {step.fires?.total ? (
              <Badge variant="soft">
                wysłano: {step.fires.delivered}/{step.fires.total}
              </Badge>
            ) : null}
          </p>
          <p className="mt-2 max-w-2xl text-sm text-text-muted line-clamp-2">{step.body}</p>
        </div>
        <div className="flex items-center gap-1">
          <label className="inline-flex items-center gap-1.5 text-xs text-text">
            <Switch
              checked={step.enabled}
              onCheckedChange={(checked) =>
                patchStep.mutate({ id: step.id, patch: { enabled: checked } })
              }
            />
            aktywny
          </label>
          {!editing ? (
            <Button type="button" variant="ghost" size="sm" onClick={onStartEdit}>
              Edytuj
            </Button>
          ) : null}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button type="button" variant="ghost" size="icon" aria-label="Usuń krok">
                <Trash2 className="size-4" aria-hidden="true" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Usunąć krok?</AlertDialogTitle>
                <AlertDialogDescription>
                  Operacja jest nieodwracalna. Wszystkie historyczne wysyłki tego kroku
                  zostają w logach, ale krok przestanie być wysyłany w przyszłych runtach.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Anuluj</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteStep.mutate(step.id)}>
                  Usuń
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      {editing ? (
        <StepEditor
          step={step}
          campaignId={campaignId}
          campaignKind={campaignKind}
          onClose={onCloseEdit}
        />
      ) : null}
    </li>
  );
}

function StepEditor({
  step,
  campaignId,
  campaignKind,
  onClose,
}: {
  step: DripStep;
  campaignId: string;
  campaignKind: string;
  onClose: () => void;
}) {
  const patchStep = usePatchStep(campaignId);
  const [channel, setChannel] = React.useState<Channel>(step.channel);
  const [anchor, setAnchor] = React.useState<Anchor>(step.anchor);
  const [offsetMinutes, setOffsetMinutes] = React.useState(step.offset_minutes);
  const [timeOfDay, setTimeOfDay] = React.useState<string | null>(step.time_of_day);
  const [body, setBody] = React.useState(step.body);
  const segs = estimateSegments(body);

  const save = async () => {
    await patchStep.mutateAsync({
      id: step.id,
      patch: { channel, anchor, offset_minutes: offsetMinutes, time_of_day: timeOfDay, body },
    });
    onClose();
  };

  return (
    <div className="mt-4 space-y-4 border-t border-border pt-4">
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Kanał" htmlFor={`ch-${step.id}`}>
          <Select id={`ch-${step.id}`} value={channel} onChange={(e) => setChannel(e.target.value as Channel)}>
            <option value="sms">SMS</option>
            <option value="email" disabled>
              Email (wkrótce)
            </option>
          </Select>
        </Field>
        <Field label="Punkt odniesienia" htmlFor={`a-${step.id}`}>
          <Select id={`a-${step.id}`} value={anchor} onChange={(e) => setAnchor(e.target.value as Anchor)}>
            {anchorsFor(campaignKind).map((a) => (
              <option key={a} value={a}>
                {ANCHOR_LABEL[a]}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Field label="Kiedy wysłać">
        <TimingPicker
          anchor={anchor}
          offsetMinutes={offsetMinutes}
          timeOfDay={timeOfDay}
          onChange={({ offset_minutes, time_of_day }) => {
            setOffsetMinutes(offset_minutes);
            setTimeOfDay(time_of_day);
          }}
        />
      </Field>
      <Field
        label="Treść"
        htmlFor={`b-${step.id}`}
        hint={`${segs.chars} zn. · ${segs.segments} seg.${segs.encoding === 'ucs-2' ? ' · Unicode' : ''}`}
      >
        <Textarea
          id={`b-${step.id}`}
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </Field>
      <p className="text-xs text-text-muted">
        Zmienne:{' '}
        <code className="rounded bg-surface px-1 py-0.5 text-[10px]">{`{{patient_name}}`}</code>{' '}
        <code className="rounded bg-surface px-1 py-0.5 text-[10px]">{`{{clinic_name}}`}</code>{' '}
        <code className="rounded bg-surface px-1 py-0.5 text-[10px]">{`{{date}}`}</code>{' '}
        <code className="rounded bg-surface px-1 py-0.5 text-[10px]">{`{{time}}`}</code>{' '}
        <code className="rounded bg-surface px-1 py-0.5 text-[10px]">{`{{doctor}}`}</code>{' '}
        <code className="rounded bg-surface px-1 py-0.5 text-[10px]">{`{{cancel_link}}`}</code>
      </p>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>
          <X className="size-4" aria-hidden="true" /> Anuluj
        </Button>
        <Button type="button" onClick={save} disabled={patchStep.isPending}>
          <Save className="size-4" aria-hidden="true" /> Zapisz krok
        </Button>
      </div>
    </div>
  );
}

function AddStepForm({
  campaignId,
  kind,
  defaultAnchor,
  defaultPosition,
  onAdded,
  onCancel,
}: {
  campaignId: string;
  kind: string;
  defaultAnchor: Anchor;
  defaultPosition: number;
  onAdded: () => void;
  onCancel: () => void;
}) {
  const addStep = useAddStep(campaignId);
  const [channel, setChannel] = React.useState<Channel>('sms');
  const [anchor, setAnchor] = React.useState<Anchor>(defaultAnchor);
  const [offsetMinutes, setOffsetMinutes] = React.useState(0);
  const [timeOfDay, setTimeOfDay] = React.useState<string | null>(null);
  const [body, setBody] = React.useState('');
  const segs = estimateSegments(body);

  const save = async () => {
    await addStep.mutateAsync({
      position: defaultPosition,
      channel,
      anchor,
      offset_minutes: offsetMinutes,
      time_of_day: timeOfDay,
      body,
      enabled: true,
    });
    onAdded();
  };

  return (
    <div className="space-y-4 rounded-[var(--radius-card)] bg-bg p-4 ring-2 ring-accent/30">
      <p className="font-display text-sm font-semibold text-text">Nowy krok</p>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Kanał" htmlFor="new-ch">
          <Select id="new-ch" value={channel} onChange={(e) => setChannel(e.target.value as Channel)}>
            <option value="sms">SMS</option>
            <option value="email" disabled>
              Email (wkrótce)
            </option>
          </Select>
        </Field>
        <Field label="Punkt odniesienia" htmlFor="new-a">
          <Select id="new-a" value={anchor} onChange={(e) => setAnchor(e.target.value as Anchor)}>
            {anchorsFor(kind).map((a) => (
              <option key={a} value={a}>
                {ANCHOR_LABEL[a]}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Field label="Kiedy wysłać">
        <TimingPicker
          anchor={anchor}
          offsetMinutes={offsetMinutes}
          timeOfDay={timeOfDay}
          onChange={({ offset_minutes, time_of_day }) => {
            setOffsetMinutes(offset_minutes);
            setTimeOfDay(time_of_day);
          }}
        />
      </Field>
      <Field
        label="Treść"
        htmlFor="new-b"
        hint={`${segs.chars} zn. · ${segs.segments} seg.${segs.encoding === 'ucs-2' ? ' · Unicode' : ''}`}
      >
        <Textarea
          id="new-b"
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Witaj {{patient_name}}, przypominamy o wizycie {{date}} {{time}}…"
        />
      </Field>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Anuluj
        </Button>
        <Button type="button" onClick={save} disabled={!body || body.length < 5 || addStep.isPending}>
          <Save className="size-4" aria-hidden="true" /> Zapisz krok
        </Button>
      </div>
    </div>
  );
}

function formatOffset(step: DripStep): string {
  if (step.offset_minutes === 0) {
    return step.time_of_day ? `O ${step.time_of_day}` : 'W momencie';
  }
  const abs = Math.abs(step.offset_minutes);
  const dir = step.offset_minutes < 0 ? 'przed' : 'po';
  if (abs % 1440 === 0) return `${abs / 1440} dni ${dir}`;
  if (abs % 60 === 0) return `${abs / 60} godz. ${dir}`;
  return `${abs} min ${dir}`;
}
